/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckPicker, SelectPicker } from 'rsuite';
import { ClipboardPaste, ListTree, Pencil, X } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Legend from '@/Components/Modals/MultiPoModal/Legend';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { renderCellByType } from './createOrdersCellConfig';
import { useStepTable, META_IDS } from './useStepTable';
import { usePasteData } from './usePasteData';
import ProductDrillDownModal from './ProductDrillDownModal';
import {
	SkuTypeChip,
	renderHeaderCell,
	renderProductMenuItem,
} from './createOrdersUiHelpers';
import { getUnitConvertedUnit } from '@/Helpers';

const TableStep = ({
	stepCfg,
	stepConfig,
	modeName,
	state,
	onStateChange,
	lists,
	productList,
	tableData,
	setTableData,
	changeQtyRef,
	setConfirmationModal,
	isLoading = false,
}) => {
	const [editingRowIndex, setEditingRowIndex] = useState(null);
	const [isProductTreeOpen, setIsProductTreeOpen] = useState(false);
	const productOptionsRef = useRef([]);

	const {
		tableLoading,
		enterTable,
		cellStores,
		tableColumns,
		tableGroups,
	} = useStepTable({
		step2: stepCfg,
		state,
		lists,
		productList,
		tableData,
		setTableData,
	});

	useEffect(() => {
		enterTable();
	}, []);

	const productOptions = useMemo(() => {
		const data = [];
		productList?.forEach((product) => {
			if (
				state?.orderType === 'PO' &&
				product?.skuType === 'INGREDIENT'
			) {
				return;
			}
			product?.variants?.forEach((variant) => {
				data.push({
					viewOnlyLabel: `${product?.productName}(${variant?.displayName})`,
					label: `${product?.productName}(${variant?.displayName})${variant?.clientVariantId ? variant?.clientVariantId : ' '}`,
					value: variant?.id,
					variantId: variant?.id,
					itemId: variant?.id,
					clientVariantId: variant?.clientVariantId,
					productId: product?.id,
					unit: product?.unit,
					skuType: product?.skuType,
					productName: product?.productName,
					variantName: variant?.displayName,
					imageUrl: product?.productImageUrl,
				});
			});
		});
		return data;
	}, [productList, state?.orderType]);
	productOptionsRef.current = productOptions;

	const pasteMode = stepCfg?.pasteMode || modeName;

	const { isProcessingClipboard, processClipboardData } = usePasteData({
		modeName: pasteMode,
		state,
		setState: onStateChange,
		lists,
		tableData,
		setTableData,
		productOptions,
		productList,
		setConfirmationModal,
		changeQtyRef,
	});

	const handleCellChange = (rowIndex, headerId, value) => {
		setTableData((prev) => {
			const rows = [...(prev?.rows || [])];
			const row = { ...rows[rowIndex] };
			const cell = row[headerId] || {};
			const original = '_originalQty' in cell ? cell._originalQty : cell?.qty;
			row[headerId] = {
				...cell,
				_originalQty: original,
				qty: value,
				_edited: String(value) !== String(original),
			};
			rows[rowIndex] = row;
			return { ...prev, rows };
		});
		changeQtyRef.current = true;
	};

	const rowTotal = (row) => {
		const sum = (tableData?.headers || []).reduce((acc, header) => {
			if (META_IDS.includes(header.id)) return acc;
			const qty = parseFloat(row?.[header.id]?.qty);
			return acc + (Number.isNaN(qty) ? 0 : qty);
		}, 0);
		return Math.round(sum * 1000) / 1000;
	};

	const rowUnit = (row) => {
		const header = (tableData?.headers || []).find(
			(h) => !META_IDS.includes(h.id) && row?.[h.id]?.unit
		);
		return header ? row[header.id].unit : '';
	};


	const handleRemoveColumn = (headerId) => {
		setTableData((prev) => ({
			...prev,
			headers: (prev?.headers || []).filter(
				(header) => `${header.id}` !== `${headerId}`
			),
			rows: (prev?.rows || []).map((row) => {
				const { [headerId]: removed, ...rest } = row;
				return rest;
			}),
		}));
	};

	const handleDeleteRow = (absoluteIndex) => {
		setTableData((prev) => {
			const rows = (prev?.rows || [])
				.filter((_, index) => index !== absoluteIndex)
				.map((row, index) => ({
					...row,
					'S.No': index + 1,
					Actions: { rowIndex: index },
				}));
			return { ...prev, rows };
		});
	};

	const handleProductSwap = (absoluteIndex, newProductId) => {
		setTableData((prev) => {
			const product = productOptionsRef.current?.find(
				(p) => p.value === newProductId
			);
			if (!product) return prev;
			const rows = [...(prev?.rows || [])];
			const oldRow = { ...rows[absoluteIndex] };
			const newRow = {
				...oldRow,
				'Item Name': product.viewOnlyLabel,
				itemId: product.value,
				SkuCode: product.clientVariantId,
				_isManuallySelected: true,
				_isNotFound: false,
				_isNewFromDropdown: false,
				_isAmbiguous: false,
				_candidates: null,
				_matchType: 'none',
			};
			(prev?.headers || []).forEach((header) => {
				if (META_IDS.includes(header.id)) return;
				newRow[header.id] = {
					...(oldRow[header.id] || {}),
					unit: getUnitConvertedUnit(null, product.unit, true),
					itemId: product.value,
					productId: product.productId,
				};
			});
			rows[absoluteIndex] = newRow;
			return { ...prev, rows };
		});
		setEditingRowIndex(null);
	};

	const customRowData = useMemo(() => {
		const renderers = {
			'S.No': (row) => (
				<span className='px-2 text-[12px]'>{row?.['S.No']}</span>
			),
			'Item Name': (row, index, key, absoluteIndex) => {
				const isReadOnly = stepCfg?.viewMode === true;
				const isEditing = !isReadOnly && editingRowIndex === absoluteIndex;
				if (!isReadOnly
					&& (row?._isNotFound || row?._isAmbiguous || isEditing)) {
					const candidateOptions = row?._isAmbiguous && row?._candidates?.length
						? row._candidates
							.map((candidate) =>
								productOptionsRef.current?.find(
									(p) => p.value === candidate.variantId
								) || {
									label: candidate.label,
									value: candidate.variantId,
									viewOnlyLabel: candidate.label,
								})
							.filter(Boolean)
						: null;
					return (
						<SelectPicker
							data={candidateOptions || productOptionsRef.current || []}
							placeholder={row?._isAmbiguous
								? 'Multiple matches — pick one'
								: 'Choose replacement'}
							onChange={(value) =>
								value && handleProductSwap(absoluteIndex, value)
							}
							onClose={() => setEditingRowIndex(null)}
							defaultOpen={isEditing}
							renderMenuItem={renderProductMenuItem}
							placement='auto'
							listProps={{ itemSize: 64 }}
							menuClassName='[&_.rs-picker-select-menu-item]:flex [&_.rs-picker-select-menu-item]:items-center [&_.rs-picker-select-menu-item]:h-16 [&_.rs-picker-select-menu-item]:!py-1.5'
							menuStyle={{ zIndex: 99999, width: 380 }}
							className='w-full'
							size='sm'
							virtualized
						/>
					);
				}
				const option = productOptionsRef.current?.find(
					(p) => p.value === row?.itemId
				);
				return (
					<div className='flex w-full max-w-[260px] items-center justify-between gap-1 px-2 py-1'>
						<div className='min-w-0 flex-1'>
							<p
								className='m-0 whitespace-normal break-words text-[13px] font-medium leading-4 text-gray-800'
								title={row?.['Item Name']}
							>
								{row?.['Item Name']}
							</p>
							<div className='mt-0.5 flex items-center gap-1.5'>
								<SkuTypeChip type={option?.skuType} />
								{row?.SkuCode && (
									<span className='text-[11px] text-gray-400'>
										{`SKU · ${row.SkuCode}`}
									</span>
								)}
							</div>
						</div>
						{!isReadOnly && (
							<button
								type='button'
								onClick={() => setEditingRowIndex(absoluteIndex)}
								className='shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600'
								title='Replace product'
							>
								<Pencil className='h-3.5 w-3.5' />
							</button>
						)}
					</div>
				);
			},
			SkuCode: (row) => (
				<span className='px-2 text-[12px] text-gray-600'>
					{row?.SkuCode ?? '-'}
				</span>
			),
			Total: (row) => {
				const total = rowTotal(row);
				const unit = rowUnit(row);
				return (
					<span className='px-2 text-[13px] font-semibold text-gray-800'>
						{total ? `${total}${unit ? ` ${unit}` : ''}` : ''}
					</span>
				);
			},
			Actions: (row, index, key, absoluteIndex) => {
				const isAdded = row?._isNewFromDropdown || row?._isManuallySelected;
				if (stepCfg?.viewMode || !isAdded) return null;
				return (
					<button
						type='button'
						onClick={() => handleDeleteRow(absoluteIndex)}
						className='flex w-full items-center justify-center rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700'
						title='Remove this product'
					>
						<X className='h-4 w-4' />
					</button>
				);
			},
		};
		(tableData?.headers || []).forEach((header) => {
			if (META_IDS.includes(header.id)) return;
			if (header._unknownColumn) {
				renderers[String(header.id)] = () => (
					<div className='flex h-full min-h-[36px] w-full items-center justify-center bg-red-100/70'>
						<span className='text-[11px] font-medium text-red-400'>
							—
						</span>
					</div>
				);
				return;
			}
			renderers[String(header.id)] = (row, index, key, absoluteIndex) => {
				const cell = row?.[header.id] || {};
				const cellType = stepCfg?.viewMode
					? 'text'
					: (stepCfg?.cellType || 'number');
				return renderCellByType(cellType, {
					value: cell?.qty,
					unit: cell?.unit,
					disabled: stepCfg?.viewMode === true,
					rowData: row,
					header,
					cellData: cell,
					onChange: (value) =>
						handleCellChange(absoluteIndex, header.id, value),
				});
			};
		});
		return renderers;
	}, [tableData?.headers, editingRowIndex, stepCfg?.cellType, stepCfg?.viewMode]);

	const selectedProductIds = useMemo(() => {
		const ids = [];
		tableData?.rows?.forEach((row) => {
			if (row?.itemId && !ids.includes(row.itemId)) ids.push(row.itemId);
			Object.values(row).forEach((cell) => {
				if (cell?.itemId && !ids.includes(cell.itemId)) {
					ids.push(cell.itemId);
				}
			});
		});
		return ids;
	}, [tableData?.rows]);

	const handleAddItems = (ids) => {
		const currentRows = [...(tableData?.rows || [])];
		let updatedRows = [];
		if (ids.length < currentRows.length) {
			updatedRows = currentRows.filter((row) => {
				const itemId =
					row?.itemId ||
					Object.values(row).find((cell) => cell?.itemId)?.itemId;
				return ids.includes(itemId);
			});
			updatedRows = updatedRows.map((row, index) => ({
				...row,
				'S.No': index + 1,
				Actions: { rowIndex: index },
			}));
		} else {
			const existing = new Set(
				currentRows.map(
					(row) =>
						row?.itemId ||
						Object.values(row).find((cell) => cell?.itemId)?.itemId
				)
			);
			ids.forEach((itemId) => {
				if (existing.has(itemId)) return;
				const product = productOptions.find((p) => p.value === itemId);
				if (!product) return;
				currentRows.push({
					Actions: { rowIndex: currentRows.length },
					'S.No': currentRows.length + 1,
					'Item Name': product.viewOnlyLabel,
					itemId: product.value,
					SkuCode: product?.clientVariantId,
					originalUnit: product?.unit,
					_isNewFromDropdown: true,
					Total: '',
					...cellStores?.reduce((acc, store) => {
						acc[`${store?.id}`] = {
							qty: '',
							unit: getUnitConvertedUnit(null, product?.unit, true),
							storeId: store?.id,
							itemId: product?.value,
							productId: product?.productId,
						};
						return acc;
					}, {}),
				});
			});
			updatedRows = currentRows.map((row, index) => ({
				...row,
				Actions: { rowIndex: index },
			}));
		}
		setTableData((prev) => {
			const headers = prev?.headers || [];
			return {
				...prev,
				headers: headers.some((header) => header.id === 'Actions')
					? headers
					: [...headers, { name: 'Actions', id: 'Actions' }],
				rows: updatedRows,
			};
		});
	};

	const tintedRows = useMemo(
		() =>
			(tableData?.rows || []).map((row) => row),
		[tableData?.rows]
	);
	const functionality = stepCfg?.functionality || [];
	const canPickFromList = functionality.includes('addProducts');
	const canPickFromTree = functionality.includes('drillDownProducts');
	const canPaste = functionality.includes('paste');
	const showLegend = functionality.includes('legend');
	const showToolbar = canPickFromList || canPickFromTree || canPaste
		|| showLegend;


	return (
		<div className='flex h-full min-h-0 flex-col gap-2 px-4 py-2.5'>
			{canPickFromTree && (
				<ProductDrillDownModal
					open={isProductTreeOpen}
					onClose={() => setIsProductTreeOpen(false)}
					productList={productList}
					selectedIds={selectedProductIds}
					onApply={handleAddItems}
				/>
			)}
			{showToolbar && (
				<div className='shrink-0'>
					<div className='flex flex-wrap items-center gap-2'>
						{canPickFromList && (
							<CheckPicker
								data={productOptions}
								placeholder='Select Products...'
								value={selectedProductIds}
								onChange={handleAddItems}
								virtualized
								listProps={{ itemSize: 64 }}
								renderMenuItem={renderProductMenuItem}
								menuClassName='[&_.rs-check-item]:flex [&_.rs-check-item]:items-center [&_.rs-check-item]:h-16 [&_.rs-checkbox-checker>label]:!py-1.5'
								menuStyle={{ zIndex: 99999 }}
								className='w-[440px] max-w-full'
								cleanable={false}
							/>
						)}
						{canPickFromTree && (
							<Button
								size='sm'
								onClick={() => setIsProductTreeOpen(true)}
								className='group flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-[13px] font-semibold text-amber-900 shadow-sm transition-all hover:border-amber-400 hover:bg-amber-100 hover:shadow'
								title='Pick products by SKU type, category and sub category'
							>
								<span className='flex size-5 items-center justify-center rounded-md bg-amber-400/90 text-gray-900 transition-colors group-hover:bg-amber-400'>
									<ListTree className='size-3.5' />
								</span>
								Add Products
								{selectedProductIds.length > 0 && (
									<span className='ml-0.5 rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-bold text-amber-800 ring-1 ring-amber-300'>
										{selectedProductIds.length}
									</span>
								)}
							</Button>
						)}
						{canPaste && (
							<Button
								variant='outline'
								size='sm'
								onClick={processClipboardData}
								disabled={
									isProcessingClipboard ||
									(tableData?.headers?.length || 0) === 0
								}
								className='flex items-center gap-2 border border-gray-300 px-3 py-2 transition-colors hover:border-blue-500 hover:bg-blue-50'
								title='Paste data from clipboard'
							>
								<ClipboardPaste className='h-4 w-4' />
								{isProcessingClipboard ? 'Processing...' : 'Paste Data'}
							</Button>
						)}
						{showLegend && (
							<div className='ml-2 [&>div]:!mt-0'>
								<Legend modeName={pasteMode} />
							</div>
						)}
					</div>
				</div>
			)}
			<div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200'>
				<VariantTable
					data={tintedRows}
					columns={tableColumns}
					groups={tableGroups}
					customRowData={customRowData}
					customCol={(column) => renderHeaderCell(column, {
						onRemoveColumn: handleRemoveColumn,
					})}
					isLoading={tableLoading || isLoading}
					virtualization={(tableData?.rows?.length || 0) > 30}
					headerRowClassName='bg-amber-50'
					headerCellClassName='bg-amber-100 !py-2 !px-2'
					customInfoLine={
						stepCfg?.emptyMessage ||
						stepConfig?.emptyMessage ||
						'No products found for the selected details'
					}
				/>
			</div>
		</div>
	);
};

export default TableStep;
